import { Box, Typography, Tooltip } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { SystemProps } from '@mui/system';
import { TournamentPlayerRole } from '@okletsplay/core';
import { TournamentPlayerEntity } from 'app/interfaces/entity';
import { sessionSelector } from 'app/store/session/selectors';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { currentTournamentSelector } from 'modules/tournaments/store/current-tournament/selectors';
import { useSelector } from 'react-redux';
import { UserCardMenu } from 'shared/components/UserCardMenu';
import { useDefaultUserMenuActions } from 'shared/hooks';
import { CardAction } from 'shared/interfaces';
import { ConnectionLabel } from '../ConnectionLabel';
import { TournamentTeamPlayerStatus } from '../TournamentTeamPlayerStatus';
import React, { FC, useMemo } from 'react';
import { UserAvatar } from 'shared/components/UserAvatar';
import { useStyles } from './TournamentTeamPlayerCard.styles';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import RedeemIcon from '@mui/icons-material/Redeem';

type TournamentTeamPlayerCardProps = {
  player: TournamentPlayerEntity;
  playerCardActions?: CardAction<TournamentPlayerEntity>[];
  isStatusEnabled?: boolean;
  isConnectionEnabled?: boolean;
} & SystemProps<Theme>;

export const TournamentTeamPlayerCard: FC<TournamentTeamPlayerCardProps> = ({
  player,
  playerCardActions = [],
  isStatusEnabled = true,
  isConnectionEnabled = false,
  ...rest
}) => {
  const { classes } = useStyles();
  const { privateMessageHandler, viewProfileAction, viewProfileHandler } =
    useDefaultUserMenuActions();

  const currentUser = useSelector(sessionSelector.activeUser);
  const isCurrentUserAdmin = useSelector(sessionSelector.isTournamentAdmin);

  const isCaptain = useMemo(() => player?.role === TournamentPlayerRole.Captain, [player?.role]);
  const isJoinedWithRewardBalance = useMemo(
    () => player?.details?.isJoinedWithRewardBalance,
    [player]
  );
  const isCurrentUser = useMemo(
    () => currentUser?.id === player.user?.id,
    [currentUser?.id, player.user?.id]
  );
  const isCurrentUserCaptain = useSelector(currentTournamentSelector.isCurrentUserCaptain);

  const actions = useMemo(() => {
    const defaultActions = [
      {
        ...viewProfileAction,
        handler: () => viewProfileHandler(player.user),
      },
    ];

    const privateMessageAction = {
      label: 'Private Message',
      isDisabled: false,
      handler: () => privateMessageHandler(player.user),
    };

    if (isCurrentUser) {
      return defaultActions;
    }

    if (isCurrentUserCaptain) {
      return [...defaultActions, ...playerCardActions, privateMessageAction];
    }

    return [...defaultActions, privateMessageAction];
  }, [
    viewProfileAction,
    isCurrentUser,
    isCurrentUserCaptain,
    viewProfileHandler,
    player.user,
    privateMessageHandler,
    playerCardActions,
  ]);

  const userNameTooltipTitle = useMemo(
    () => player?.user?.userName ?? '',
    [player?.user?.userName]
  );

  const joinedWithRewardBalanceTooltipTitle = useMemo(
    () => (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="caption">Joined with Reward Balance</Typography>
        <Typography variant="caption">(the icon is visible only for tournament admins)</Typography>
      </Box>
    ),
    []
  );

  const layout = (
    <Box className={classes.root} {...rest}>
      <Box display="flex" alignItems="center" width="80%">
        <UserAvatar user={player?.user} />
        <Box pl={2} pr={2} className={classes.usernameContainer}>
          <Tooltip title={userNameTooltipTitle}>
            <Typography
              className={classNames(classes.username, classes.text)}
              lineHeight={1}
              mr={1}
            >
              <>
                {player?.user?.userName}
                {isConnectionEnabled && <ConnectionLabel player={player} />}
              </>
            </Typography>
          </Tooltip>
          {isCaptain ? (
            <Tooltip title="Captain" placement="top">
              <KeyboardDoubleArrowUpIcon className={classes.arrowIcon} />
            </Tooltip>
          ) : null}
          {isJoinedWithRewardBalance && isCurrentUserAdmin ? (
            <Tooltip title={joinedWithRewardBalanceTooltipTitle} placement="top">
              <RedeemIcon />
            </Tooltip>
          ) : null}
        </Box>
      </Box>
      <Box display="flex" alignItems="center">
        {isStatusEnabled && <TournamentTeamPlayerStatus status={player.status} />}
        <Box pl={1}>
          <UserCardMenu readonly />
        </Box>
      </Box>
    </Box>
  );

  if (!actions.length) {
    return layout;
  }

  if (isEmpty(player?.user)) {
    return null;
  }

  return (
    <UserCardMenu data={player} actions={actions}>
      {layout}
    </UserCardMenu>
  );
};
